import { Link } from 'react-router-dom'
import { useJanelaDoComite } from '../../api/queries/gmuds'
import { useMinhasPendencias } from '../../api/queries/usuarios'
import { dataHoraPorExtenso } from '../../lib/datas'
import { AvisoDeErro, Esqueleto } from '../../components/Estados'
import { IconeSeta } from '../../components/Icones'
import { Cartao } from '../../components/ListaDeDados'
import { TEXTOS } from '../../lib/glossario'
import type { Pendencia } from '../../types/usuario'

/**
 * O que está esperando ESTA pessoa.
 *
 * **O bloco anterior mostrava "esperando alguém", e era honesto sobre isso** — a consulta era por
 * estado, não por pessoa. O presidente do comitê via as solicitações em execução da contratada, e o
 * executor via as que esperam decisão do comitê. Com sete pessoas testando, "o que é meu?" vira a
 * primeira pergunta de todo login, e ela não tinha resposta.
 *
 * **O bloco some quando não há nada.** Uma caixa de entrada que nunca esvazia deixa de ser lida —
 * e é por isso que contagem zero não vira linha, e que mês já fechado não aparece como pendência.
 *
 * **Nada aqui autoriza nada.** O que a pessoa pode fazer com uma solicitação continua vindo de
 * `/gmuds/{id}/transicoes-possiveis`, na tela de detalhe. Este bloco só encurta o caminho até lá.
 *
 * @returns o cartão de pendências, ou nada quando a caixa está vazia
 */
export function EsperandoVoce() {
  const { data: pendencias, isPending, isError, error } = useMinhasPendencias()

  if (isPending) {
    return (
      <Cartao titulo={TEXTOS.pendencias.titulo}>
        <Esqueleto linhas={3} />
      </Cartao>
    )
  }

  if (isError) {
    return (
      <Cartao titulo={TEXTOS.pendencias.titulo}>
        <AvisoDeErro erro={error} titulo={TEXTOS.pendencias.naoCarregou} />
      </Cartao>
    )
  }

  // Some da tela. É deliberado: o bloco existe para dizer o que falta, e "não falta nada" não
  // precisa ocupar a primeira dobra do painel todos os dias.
  if (!pendencias || pendencias.length === 0) {
    return null
  }

  return (
    <Cartao titulo={TEXTOS.pendencias.titulo} descricao={TEXTOS.pendencias.ajuda}>
      <ul className="divide-y divide-slate-100">
        {pendencias.map((pendencia) => (
          <LinhaDaPendencia key={pendencia.codigo} pendencia={pendencia} />
        ))}
      </ul>
      {pendencias.some((pendencia) => pendencia.codigo.startsWith('CAB_')) && <ProximaAnalise />}
    </Cartao>
  )
}

/** Uma pendência: quantos, o que é, e por que importa. */
function LinhaDaPendencia({ pendencia }: { pendencia: Pendencia }) {
  return (
    <li>
      <Link
        to={pendencia.endereco}
        className="flex items-start gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-niteroi-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-niteroi-600"
      >
        {/* O número primeiro, e grande: é o que a pessoa lê antes de decidir se abre. */}
        <span className="min-w-[2.5rem] shrink-0 text-2xl font-bold tabular-nums text-niteroi-700">
          {pendencia.quantos}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-slate-900">{pendencia.titulo}</span>
          <span className="mt-0.5 block max-w-prose text-xs text-slate-600">{pendencia.ajuda}</span>
        </span>
        <IconeSeta className="mt-1 h-4 w-4 shrink-0 rotate-180 text-slate-400" />
      </Link>
    </li>
  )
}

/**
 * Quando é a próxima análise do comitê — a data que o painel omitia.
 *
 * "Há 3 na mesa" sem dizer se a análise é amanhã ou daqui a cinco dias não responde à pergunta
 * do presidente ("existe reunião a preparar?"). A data já existia no sistema e só aparecia em
 * /cab e no calendário. Só leitura de configuração; a reunião continua fora do sistema.
 */
function ProximaAnalise() {
  const { data: janela } = useJanelaDoComite()
  if (!janela) {
    return null
  }
  return (
    <p className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-700">
      {janela.proximaAbertura
        ? TEXTOS.pendencias.proximaAnalise(dataHoraPorExtenso(janela.proximaAbertura))
        : TEXTOS.pendencias.semProximaAnalise}{' '}
      <Link to="/cab/calendario" className="font-medium text-niteroi-700 underline underline-offset-2">
        {TEXTOS.pendencias.proximaAnaliseAjuda}
      </Link>
    </p>
  )
}
